import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreVertical, X } from "lucide-react";
import ReceiptItemCard from "../components/ReceiptItemCard";
import { useToast } from "../contexts/ToastContext";
import { api, setClaimPortion } from "../lib/api";
import { formatMoney } from "../lib/format";
import { useReceiptCancelMutation } from "../lib/hooks/useReceiptCancelMutation";
import { useReceiptPhoto } from "../lib/hooks/useReceiptPhoto";
import { z } from "zod";
import { MemberOut, ReceiptDetailOut } from "../lib/schemas";
import { webApp, useBackButton, useMainButton, haptic } from "../lib/telegram";
import { AvatarGroup } from "../ui/AvatarGroup";
import { Sheet } from "../ui/Sheet";

export default function ReceiptDetail() {
  const { id, receiptId } = useParams<{ id: string; receiptId: string }>();
  const groupId = Number(id);
  const nav = useNavigate();
  const showToast = useToast();
  const qc = useQueryClient();

  const [submitting, setSubmitting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tipDraft, setTipDraft] = useState("0.00");
  const [splitDraft, setSplitDraft] = useState("1");
  const [editingField, setEditingField] = useState<"tip" | "split" | null>(null);
  const cancelMutation = useReceiptCancelMutation();

  const pendingRef = useRef<Record<string, unknown>>({});
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const portionFlushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queryKey = ["receipt", receiptId];

  const { data: receipt, isLoading, error } = useQuery({
    queryKey,
    queryFn: () =>
      api<ReceiptDetailOut>(
        "GET",
        `/chats/${groupId}/receipts/${receiptId}`,
      ).then((raw) => ReceiptDetailOut.parse(raw)),
    refetchInterval: (query) => {
      const data = query.state.data as ReceiptDetailOut | undefined;
      if (data?.status === "finalized") return false;
      return Object.keys(pendingRef.current).length > 0 ? false : 3000;
    },
    staleTime: 3000,
  });

  const readOnly = receipt?.status === "finalized";

  const { url: photoUrl, isLoading: photoLoading } = useReceiptPhoto(receiptId, readOnly ?? false);

  const { data: allMembers = [] } = useQuery({
    queryKey: ["members", groupId],
    queryFn: () =>
      api<z.infer<typeof MemberOut>[]>("GET", `/chats/${groupId}/members`)
        .then((raw) => MemberOut.array().parse(raw)),
    staleTime: 60_000,
  });

  // Get current user's DB id from the payer/uploader data
  // We use the receipt's uploader telegram_id to find who we are.
  // The Mini App's Telegram ID is available via webApp.initDataUnsafe.user.id
  const myTgId = webApp.initDataUnsafe?.user?.id ?? 0;
  const myDbId = receipt
    ? (receipt.items.flatMap(i => i.claimants).find(c => c.telegram_id === myTgId)?.id
      ?? (receipt.uploader.telegram_id === myTgId ? receipt.uploader.id : null)
      ?? (receipt.payer?.telegram_id === myTgId ? receipt.payer.id : null))
    : null;

  const payerId = receipt?.payer?.id ?? receipt?.uploader?.id ?? null;
  const isMyTurnToPay = !readOnly && myDbId !== null && payerId !== null && myDbId === payerId;

  const canCancel = receipt?.status === "ready" &&
    (myTgId === receipt.uploader.telegram_id || myTgId === (receipt.payer?.telegram_id ?? -1));

  const unclaimedCount = receipt
    ? receipt.items.filter(item => !item.muted && item.claimants.length === 0).length
    : 0;

  async function toggleClaim(itemIdx: number) {
    haptic.selection();
    try {
      const updated = await api<ReceiptDetailOut>(
        "POST",
        `/chats/${groupId}/receipts/${receiptId}/items/${itemIdx}/claim`,
      );
      qc.setQueryData(queryKey, updated);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to update claim", { type: "error" });
    }
  }

  async function toggleGuestClaim(guestId: number) {
    haptic.selection();
    try {
      const updated = await api<ReceiptDetailOut>(
        "POST",
        `/chats/${groupId}/receipts/${receiptId}/guests/${guestId}/claim`,
      );
      qc.setQueryData(queryKey, updated);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to update guest claim", { type: "error" });
    }
  }

  async function patchItem(itemIdx: number, body: { muted?: boolean; phantom_count?: number }) {
    try {
      const updated = await api<ReceiptDetailOut>(
        "PATCH",
        `/chats/${groupId}/receipts/${receiptId}/items/${itemIdx}`,
        { json: body },
      );
      qc.setQueryData(queryKey, updated);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to update item", { type: "error" });
    }
  }

  const flushPatch = useCallback(async () => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
    }
    const body = { ...pendingRef.current };
    pendingRef.current = {};
    if (Object.keys(body).length === 0) return;
    try {
      const updated = await api<ReceiptDetailOut>(
        "PATCH",
        `/chats/${groupId}/receipts/${receiptId}`,
        { json: body },
      );
      qc.setQueryData(["receipt", receiptId], updated);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to save", { type: "error" });
      void qc.invalidateQueries({ queryKey: ["receipt", receiptId] });
    }
  }, [groupId, receiptId, qc, showToast]);

  function optimisticPatch(patch: Partial<ReceiptDetailOut>, json: Record<string, unknown>) {
    void qc.cancelQueries({ queryKey });
    qc.setQueryData<ReceiptDetailOut>(queryKey, (prev) => (prev ? { ...prev, ...patch } : prev));
    Object.assign(pendingRef.current, json);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => { void flushPatch(); }, 400);
  }

  function handleCancelReceipt() {
    setMenuOpen(false);
    cancelMutation.mutate(
      { chatId: groupId, receiptId: receiptId ?? "" },
      { onSuccess: () => nav(`/groups/${groupId}`, { replace: true }) },
    );
  }

  async function handleConfirm() {
    if (!receipt || submitting) return;
    await flushPatch();
    setSubmitting(true);
    webApp.MainButton.showProgress(false);
    try {
      await api("POST", `/chats/${groupId}/receipts/${receiptId}/confirm`);
      haptic.success();
      showToast("Receipt confirmed!", { type: "success" });
      void qc.invalidateQueries({ queryKey: ["pending"] });
      void qc.invalidateQueries({ queryKey: ["history"] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      haptic.error();
      showToast(e instanceof Error ? e.message : "Confirm failed", { type: "error" });
    } finally {
      setSubmitting(false);
      webApp.MainButton.hideProgress();
    }
  }

  const confirmRef = useRef(handleConfirm);
  confirmRef.current = handleConfirm;

  useBackButton(useCallback(() => nav(`/groups/${groupId}`), [nav, groupId]));

  useMainButton(
    "Confirm",
    isMyTurnToPay && !submitting && receipt?.status === "ready",
    useCallback(() => { void confirmRef.current(); }, []),
  );

  // Status-based redirects must live in an effect — never navigate during render.
  useEffect(() => {
    if (!receipt) return;
    if (receipt.status === "expired") {
      nav(`/groups/${groupId}`, { replace: true });
    }
  }, [receipt?.status, groupId, nav]);

  useEffect(() => () => { void flushPatch(); }, [flushPatch]);
  useEffect(() => () => { if (portionFlushTimer.current) clearTimeout(portionFlushTimer.current); }, []);

  useEffect(() => {
    if (!receipt) return;
    if (editingField !== "tip") setTipDraft(Number(receipt.tip).toFixed(2));
  }, [receipt?.tip]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!receipt) return;
    if (editingField !== "split") setSplitDraft(String(receipt.tip_split_count));
  }, [receipt?.tip_split_count]); // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 flex items-center justify-center">
        <p className="text-danger text-sm">Failed to load receipt</p>
      </div>
    );
  }

  if (isLoading || !receipt) {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 flex items-center justify-center">
        <p className="text-muted text-sm">Loading receipt…</p>
      </div>
    );
  }

  if (receipt.status === "parsing") {
    return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-32">
        <h1 className="font-sans text-base font-bold text-fg mb-6 text-center">
          Receipt
        </h1>
        <div className="bg-surface border border-hairline rounded-2xl shadow-card p-6 flex flex-col items-center gap-3 text-center">
          <span className="text-4xl">⏳</span>
          <p className="text-fg font-medium text-sm">Parsing receipt…</p>
          <p className="text-muted text-xs">This can take up to a minute.</p>
        </div>
      </div>
    );
  }

  if (receipt.status === "failed") {
    return (
      <ReceiptOcrFailedCard
        groupId={groupId}
        receiptId={receiptId!}
        parseError={receipt.parse_error ?? null}
        onRetrySuccess={() => qc.invalidateQueries({ queryKey })}
      />
    );
  }

  function handlePayerToggle(memberId: number | string) {
    void (async () => {
      try {
        const updated = await api<ReceiptDetailOut>(
          "PATCH",
          `/chats/${groupId}/receipts/${receiptId}`,
          { json: { payer_id: Number(memberId) } },
        );
        qc.setQueryData(queryKey, updated);
      } catch { /* ignore */ }
    })();
  }

  const tipAmount = Number(receipt.tip);
  const splitCount = receipt.tip_split_count;

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-36">
      <div className="relative mb-1 flex items-center justify-center">
        <h1 className="text-base font-bold text-fg text-center">
          {receipt.description}
        </h1>
        {canCancel && (
          <button
            type="button"
            className="absolute right-0 top-1/2 -translate-y-1/2 p-1 text-muted active:opacity-60"
            onClick={() => setMenuOpen(true)}
            aria-label="Receipt options"
          >
            <MoreVertical size={18} />
          </button>
        )}
      </div>

      {!readOnly && (
        <p className="font-sans text-xs font-medium text-muted text-center mb-5">
          Tap items you ordered
        </p>
      )}

      {readOnly && (
        <p className="font-sans text-xs font-medium text-muted text-center mb-5">
          Confirmed
        </p>
      )}

      <Sheet open={menuOpen} onClose={() => setMenuOpen(false)}>
        <button
          type="button"
          className="w-full flex items-center gap-3 px-4 py-4 text-danger text-sm font-medium active:bg-surface transition-colors"
          onClick={handleCancelReceipt}
        >
          <X size={16} />
          Cancel receipt
        </button>
      </Sheet>

      {/* Receipt photo (finalized only) */}
      {readOnly && (
        <div className="mb-5">
          {photoLoading && (
            <div className="border border-hairline rounded-2xl w-full aspect-[4/3] flex items-center justify-center bg-surface">
              <p className="text-muted text-xs">Loading photo…</p>
            </div>
          )}
          {!photoLoading && photoUrl && (
            <img
              src={photoUrl}
              alt="Receipt photo"
              className="w-full border border-hairline rounded-2xl object-contain max-h-80"
            />
          )}
        </div>
      )}

      {/* Unclaimed banner for payer */}
      {isMyTurnToPay && unclaimedCount > 0 && (
        <div className="bg-warning/10 border border-warning/50 rounded-2xl px-4 py-3 mb-4 text-sm text-fg">
          {unclaimedCount} item{unclaimedCount !== 1 ? "s" : ""} unclaimed → will go to you
        </div>
      )}

      {/* Waiting pill for non-payer */}
      {!readOnly && !isMyTurnToPay && payerId !== null && (
        <div className="mb-4 px-4 py-3 bg-surface border border-hairline rounded-2xl shadow-card text-center">
          <p className="text-muted text-xs font-sans font-medium">
            Waiting for {receipt.payer?.first_name ?? receipt.uploader.first_name} to confirm
          </p>
        </div>
      )}

      {/* Item list: grouped sections when guests are present, flat list otherwise */}
      {receipt.guests.length > 0 ? (
        <>
          {receipt.guests.map((guest) => {
            const guestItems = receipt.items.filter((it) => guest.item_idxs.includes(it.idx));
            const activeGuestItems = guestItems.filter((it) => !it.muted);
            const allClaimed = !readOnly && activeGuestItems.length > 0 &&
              activeGuestItems.every((it) => it.claimants.some((c) => c.id === myDbId));
            return (
              <div key={guest.id} className="mb-4 bg-surface border border-hairline rounded-2xl shadow-card p-3">
                {/* Guest section header */}
                <div className="flex items-center justify-between px-1 pb-2.5 mb-1 border-b border-hairline">
                  <span className="text-xs font-medium text-muted">
                    {guest.label}
                  </span>
                  <span className="text-sm font-mono font-medium text-fg [font-variant-numeric:tabular-nums]">
                    {formatMoney(guest.total, receipt.currency)}
                  </span>
                </div>

                {/* Items in this guest block */}
                {guestItems.map((item) => {
                  const isClaimed = myDbId !== null && item.claimants.some(c => c.id === myDbId);
                  const myClaimant = myDbId !== null
                    ? item.claimants.find(c => c.id === myDbId)
                    : undefined;
                  const myPortion = myClaimant?.portion ?? null;
                  return (
                    <ReceiptItemCard
                      key={item.idx}
                      item={{ name: item.name, price: item.price }}
                      quantity={item.quantity}
                      claimed={isClaimed}
                      claimedBy={item.claimants}
                      muted={item.muted}
                      phantomCount={item.phantom_count}
                      myDbId={myDbId}
                      myPortion={myPortion}
                      onToggleClaim={() => void toggleClaim(item.idx)}
                      onMute={() => void patchItem(item.idx, { muted: true })}
                      onUnmute={() => void patchItem(item.idx, { muted: false })}
                      onGuestChange={(count) => void patchItem(item.idx, { phantom_count: count })}
                      onSetPortion={(portion) => {
                        if (portionFlushTimer.current) clearTimeout(portionFlushTimer.current);
                        portionFlushTimer.current = setTimeout(() => {
                          void (async () => {
                            try {
                              await setClaimPortion(groupId, receiptId ?? "", item.idx, portion);
                              void qc.invalidateQueries({ queryKey });
                            } catch (e) {
                              showToast(e instanceof Error ? e.message : "Failed to set portion", { type: "error" });
                            }
                          })();
                        }, 400);
                      }}
                      currency={receipt.currency}
                      readOnly={readOnly}
                    />
                  );
                })}

                {/* Service sub-line */}
                {Number(receipt.service_charge) > 0 && (
                  <div className="flex items-center justify-between px-1 py-1.5 mt-1 border-t border-hairline">
                    <span className="text-xs text-muted">
                      + Service{receipt.service_pct ? ` (${receipt.service_pct}%)` : ""}
                    </span>
                    <span className="text-xs text-muted font-mono [font-variant-numeric:tabular-nums]">
                      {formatMoney(guest.service, receipt.currency)}
                    </span>
                  </div>
                )}

                {/* Claim-all button */}
                {!readOnly && (
                  <div className="pt-2.5 mt-1 border-t border-hairline">
                    <button
                      type="button"
                      onClick={() => void toggleGuestClaim(guest.id)}
                      className={[
                        "w-full py-2.5 text-xs font-semibold rounded-xl border transition-colors",
                        allClaimed
                          ? "bg-fg text-bg border-fg"
                          : "bg-surface-2 text-fg border-hairline active:opacity-60",
                      ].join(" ")}
                    >
                      {allClaimed ? `✓ Claimed ${guest.label}` : `Claim all · ${guest.label}`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Ungrouped items (idx not in any guest) */}
          {receipt.items
            .filter((it) => !receipt.guests.some((g) => g.item_idxs.includes(it.idx)))
            .map((item) => {
              const isClaimed = myDbId !== null && item.claimants.some(c => c.id === myDbId);
              const myClaimant = myDbId !== null
                ? item.claimants.find(c => c.id === myDbId)
                : undefined;
              const myPortion = myClaimant?.portion ?? null;
              return (
                <ReceiptItemCard
                  key={item.idx}
                  item={{ name: item.name, price: item.price }}
                  quantity={item.quantity}
                  group={item.group ?? undefined}
                  claimed={isClaimed}
                  claimedBy={item.claimants}
                  muted={item.muted}
                  phantomCount={item.phantom_count}
                  myDbId={myDbId}
                  myPortion={myPortion}
                  onToggleClaim={() => void toggleClaim(item.idx)}
                  onMute={() => void patchItem(item.idx, { muted: true })}
                  onUnmute={() => void patchItem(item.idx, { muted: false })}
                  onGuestChange={(count) => void patchItem(item.idx, { phantom_count: count })}
                  onSetPortion={(portion) => {
                    if (portionFlushTimer.current) clearTimeout(portionFlushTimer.current);
                    portionFlushTimer.current = setTimeout(() => {
                      void (async () => {
                        try {
                          await setClaimPortion(groupId, receiptId ?? "", item.idx, portion);
                          void qc.invalidateQueries({ queryKey });
                        } catch (e) {
                          showToast(e instanceof Error ? e.message : "Failed to set portion", { type: "error" });
                        }
                      })();
                    }, 400);
                  }}
                  currency={receipt.currency}
                  readOnly={readOnly}
                />
              );
            })}
        </>
      ) : (
        /* Flat item list (no guests) */
        receipt.items.map((item) => {
          const isClaimed = myDbId !== null && item.claimants.some(c => c.id === myDbId);
          const myClaimant = myDbId !== null
            ? item.claimants.find(c => c.id === myDbId)
            : undefined;
          const myPortion = myClaimant?.portion ?? null;
          return (
            <ReceiptItemCard
              key={item.idx}
              item={{ name: item.name, price: item.price }}
              quantity={item.quantity}
              group={item.group ?? undefined}
              claimed={isClaimed}
              claimedBy={item.claimants}
              muted={item.muted}
              phantomCount={item.phantom_count}
              myDbId={myDbId}
              myPortion={myPortion}
              onToggleClaim={() => void toggleClaim(item.idx)}
              onMute={() => void patchItem(item.idx, { muted: true })}
              onUnmute={() => void patchItem(item.idx, { muted: false })}
              onGuestChange={(count) => void patchItem(item.idx, { phantom_count: count })}
              onSetPortion={(portion) => {
                if (portionFlushTimer.current) clearTimeout(portionFlushTimer.current);
                portionFlushTimer.current = setTimeout(() => {
                  void (async () => {
                    try {
                      await setClaimPortion(groupId, receiptId ?? "", item.idx, portion);
                      void qc.invalidateQueries({ queryKey });
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : "Failed to set portion", { type: "error" });
                    }
                  })();
                }, 400);
              }}
              currency={receipt.currency}
              readOnly={readOnly}
            />
          );
        })
      )}

      {/* Tax / tip / total footer */}
      <div className="mt-4 bg-surface border border-hairline rounded-2xl shadow-card px-4 py-4 space-y-2">
        {/* Tax row — info only, tax is already included in item prices */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted">Tax (incl.)</span>
          <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
            {formatMoney(receipt.tax, receipt.currency)}
          </span>
        </div>

        {/* Service charge row — shown when OCR detected a pre-included service fee */}
        {Number(receipt.service_charge) > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted">
              Service{receipt.service_pct ? ` (${receipt.service_pct}%)` : ""} (incl.)
            </span>
            <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
              {formatMoney(receipt.service_charge, receipt.currency)}
            </span>
          </div>
        )}

        {/* Tip section */}
        {!readOnly ? (
          <>
            {/* Tip input row */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Tip</span>
              <div className="flex items-center gap-1.5">
                <span className="text-muted text-xs">{receipt.currency}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  value={tipDraft}
                  className="font-mono [font-variant-numeric:tabular-nums] text-fg text-right bg-surface-2 rounded-lg border border-hairline px-2 py-1 w-24 outline-none focus:border-fg"
                  onFocus={() => setEditingField("tip")}
                  onChange={(e) => {
                    const val = e.target.value.replace(",", ".");
                    if (!/^\d*\.?\d{0,2}$/.test(val)) return;
                    setTipDraft(val);
                    const n = parseFloat(val);
                    if (isFinite(n) && n >= 0) {
                      optimisticPatch({ tip: n.toFixed(2) }, { tip: n.toFixed(2) });
                    }
                  }}
                  onBlur={() => {
                    setEditingField(null);
                    const n = Math.max(0, parseFloat(tipDraft) || 0);
                    const normalized = n.toFixed(2);
                    setTipDraft(normalized);
                    optimisticPatch({ tip: normalized }, { tip: normalized });
                  }}
                  aria-label="Tip amount"
                />
              </div>
            </div>

            {/* Split count input row */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Split</span>
              <div className="flex items-center gap-1.5">
                <span className="text-muted text-xs">people</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={splitDraft}
                  className="font-mono [font-variant-numeric:tabular-nums] text-fg text-right bg-surface-2 rounded-lg border border-hairline px-2 py-1 w-24 outline-none focus:border-fg"
                  onFocus={() => setEditingField("split")}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!/^\d*$/.test(val)) return;
                    setSplitDraft(val);
                    const n = parseInt(val, 10);
                    if (Number.isInteger(n) && n >= 1) {
                      optimisticPatch({ tip_split_count: n }, { tip_split_count: n });
                    }
                  }}
                  onBlur={() => {
                    setEditingField(null);
                    const n = Math.max(1, parseInt(splitDraft, 10) || 1);
                    setSplitDraft(String(n));
                    optimisticPatch({ tip_split_count: n }, { tip_split_count: n });
                  }}
                  aria-label="Split count"
                />
              </div>
            </div>

            {/* Per-person tip helper */}
            {tipAmount > 0 && (
              <p className="font-sans text-xs font-medium text-faint text-right">
                ~{formatMoney(receipt.per_person_tip, receipt.currency)} per person
              </p>
            )}
          </>
        ) : (
          <>
            {/* Read-only tip row */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Tip</span>
              <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                {formatMoney(receipt.tip, receipt.currency)}
              </span>
            </div>

            {/* Read-only split count row */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted">Split</span>
              <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                {splitCount} {splitCount === 1 ? "person" : "people"}
              </span>
            </div>

            {/* Per-person tip */}
            {tipAmount > 0 && (
              <p className="font-sans text-xs font-medium text-faint text-right">
                ~{formatMoney(receipt.per_person_tip, receipt.currency)} per person
              </p>
            )}
          </>
        )}

        {/* Receipt total row (OCR printed total) */}
        <div className="flex items-center justify-between text-sm pt-1 border-t border-hairline">
          <span className="text-muted">Receipt total</span>
          <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
            {formatMoney(receipt.receipt_total, receipt.currency)}
          </span>
        </div>

      </div>

      {/* Per-person breakdown */}
      {receipt.shares.length > 0 ? (
        <div className="mt-4 bg-surface border border-hairline rounded-2xl shadow-card px-4 py-4 space-y-2">
          <p className="font-sans text-xs font-medium text-muted mb-1">
            Per person
          </p>
          {receipt.shares.map((s) => (
            <div key={s.user_id} className="flex items-center justify-between text-sm">
              <span className="text-fg">{s.first_name}</span>
              <span className="text-fg font-mono [font-variant-numeric:tabular-nums]">
                {formatMoney(s.amount, receipt.currency)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        !readOnly && (
          <p className="mt-4 text-center font-sans text-xs font-medium text-faint">
            Tap items to see your share
          </p>
        )
      )}

      {/* Paid by */}
      <div className="mt-4">
        <p className="font-sans text-xs font-medium text-muted mb-3">
          Paid by
        </p>
        <AvatarGroup
          members={allMembers.map(m => ({ id: m.id, display_name: m.first_name }))}
          selectedIds={payerId !== null ? [payerId] : []}
          onToggle={!readOnly ? handlePayerToggle : undefined}
          size="md"
          maxVisible={6}
        />
      </div>
    </div>
  );
}

function ReceiptOcrFailedCard({
  groupId,
  receiptId,
  parseError,
  onRetrySuccess,
}: {
  groupId: number;
  receiptId: string;
  parseError: string | null;
  onRetrySuccess: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const [noPhoto, setNoPhoto] = useState(false);
  const showToast = useToast();

  async function handleRetry() {
    setRetrying(true);
    try {
      await api("POST", `/chats/${groupId}/receipts/${receiptId}/retry`);
      onRetrySuccess();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 409) {
        setNoPhoto(true);
      } else {
        showToast("Retry failed — please try again");
      }
    } finally {
      setRetrying(false);
    }
  }

  return (
    <div className="min-h-screen bg-bg px-4 pt-6 pb-32">
      <h1 className="font-sans text-base font-bold text-fg mb-6 text-center">
        Receipt
      </h1>
      <div className="bg-surface border border-hairline rounded-2xl shadow-card p-6 flex flex-col items-center gap-3 text-center">
        <span className="text-4xl">❌</span>
        <p className="text-fg font-medium text-sm">Couldn't parse this receipt</p>
        {parseError && (
          <p className="text-muted text-xs">{parseError}</p>
        )}
        {noPhoto ? (
          <p className="text-muted text-xs mt-1">
            No archived photo available — please re-upload the receipt.
          </p>
        ) : (
          <button
            className="mt-2 w-full bg-fg text-bg text-sm font-semibold rounded-xl py-2.5 px-4 active:opacity-70 disabled:opacity-40 transition-opacity"
            onClick={() => void handleRetry()}
            disabled={retrying}
          >
            {retrying ? "Retrying…" : "Retry"}
          </button>
        )}
      </div>
    </div>
  );
}
